// src/components/forms/shared/AccessibilityHoverStyles.ts
import { Theme } from '@mui/material/styles';
import { colors } from '../../../theme/theme';

// All return {} when Accessibility Mode is off or the field opted out -
// callers spread the result into an existing `:hover` block, additive only.

export interface InputAccessibilityHoverProps {
  accessibilityHoverDisabled?: boolean;
  accessibilityHoverBorder?: string;
  accessibilityHoverBorderColor?: string;
  accessibilityHoverBackgroundColor?: string;
  accessibilityHoverColor?: string;
  accessibilityHoverGlow?: boolean;
}

// Text inputs, the CustomSelect trigger, the DatePicker wrapper.
export const getAccessibilityInputHover = (
  theme: Theme,
  accessibilityMode: boolean,
  props: InputAccessibilityHoverProps = {}
) => {
  const {
    accessibilityHoverDisabled = false,
    accessibilityHoverBorder,
    accessibilityHoverBorderColor,
    accessibilityHoverBackgroundColor,
    accessibilityHoverColor,
    accessibilityHoverGlow = true,
  } = props;

  if (!accessibilityMode || accessibilityHoverDisabled) return {};

  const isDark = theme.palette.mode === 'dark';
  const mode = isDark ? 'dark' : 'light';

  const ringColor =
    accessibilityHoverBorderColor ||
    colors.accessibility.hover.inputRingColor[mode];
  const bgColor =
    accessibilityHoverBackgroundColor ||
    colors.accessibility.hover.inputBackgroundColor[mode];
  const textColor =
    accessibilityHoverColor || colors.accessibility.hover.inputTextColor[mode];

  return {
    borderColor: ringColor,
    ...(accessibilityHoverBorder && { border: accessibilityHoverBorder }),
    backgroundColor: bgColor,
    color: textColor,
    // Single bold ring - border width itself stays whatever the field
    // already uses (3px / 4px-error), this only adds the colour ring.
    ...(accessibilityHoverGlow && {
      boxShadow: `0 0 0 1px ${ringColor}`,
    }),
  };
};

export interface RadioAccessibilityHoverProps {
  accessibilityHoverDisabled?: boolean;
  accessibilityHoverBackgroundColor?: string;
  accessibilityHoverColor?: string;
  accessibilityHoverBorderRadius?: string;
}

// Pill wrap for the radio label + dot.
export const getAccessibilityRadioHover = (
  theme: Theme,
  accessibilityMode: boolean,
  props: RadioAccessibilityHoverProps = {}
) => {
  const {
    accessibilityHoverDisabled = false,
    accessibilityHoverBackgroundColor,
    accessibilityHoverColor,
    accessibilityHoverBorderRadius,
  } = props;

  if (!accessibilityMode || accessibilityHoverDisabled) return {};

  const mode = theme.palette.mode === 'dark' ? 'dark' : 'light';

  const bg =
    accessibilityHoverBackgroundColor ||
    colors.accessibility.hover.radioBackgroundColor[mode];

  // RadioLabel carries permanent padding so the pill has room to render -
  // hover just swaps the fill colour, no box-shadow needed.
  return {
    backgroundColor: bg,
    color:
      accessibilityHoverColor || colors.accessibility.hover.radioColor[mode],
    borderRadius:
      accessibilityHoverBorderRadius ||
      colors.accessibility.hover.radioBorderRadius,
  };
};

export interface ToggleAccessibilityHoverProps {
  accessibilityHoverDisabled?: boolean;
  accessibilityHoverBackgroundColor?: string;
  accessibilityHoverDotColor?: string;
  accessibilityHoverDotExpand?: boolean;
}

export const getAccessibilityToggleTrackHover = (
  theme: Theme,
  accessibilityMode: boolean,
  props: ToggleAccessibilityHoverProps = {}
) => {
  const {
    accessibilityHoverDisabled = false,
    accessibilityHoverBackgroundColor,
  } = props;
  if (!accessibilityMode || accessibilityHoverDisabled) return {};
  const mode = theme.palette.mode === 'dark' ? 'dark' : 'light';
  return {
    backgroundColor:
      accessibilityHoverBackgroundColor ||
      colors.accessibility.hover.toggleBackgroundColor[mode],
  };
};

export const getAccessibilityToggleThumbHover = (
  accessibilityMode: boolean,
  props: ToggleAccessibilityHoverProps = {}
) => {
  const {
    accessibilityHoverDisabled = false,
    accessibilityHoverDotColor,
    accessibilityHoverDotExpand = true,
  } = props;
  if (!accessibilityMode || accessibilityHoverDisabled) return {};
  return {
    ...(accessibilityHoverDotColor && {
      backgroundColor: accessibilityHoverDotColor,
    }),
    ...(accessibilityHoverDotExpand && {
      transform: 'scale(1.18)',
    }),
  };
};

export interface ListItemAccessibilityHoverProps {
  accessibilityHoverDisabled?: boolean;
  accessibilityHoverBackgroundColor?: string;
  accessibilityHoverColor?: string;
}

// CustomSelect's open option list - tune-up folded in alongside the trigger.
export const getAccessibilityListItemHover = (
  theme: Theme,
  accessibilityMode: boolean,
  props: ListItemAccessibilityHoverProps = {}
) => {
  const {
    accessibilityHoverDisabled = false,
    accessibilityHoverBackgroundColor,
    accessibilityHoverColor,
  } = props;
  if (!accessibilityMode || accessibilityHoverDisabled) return {};
  const mode = theme.palette.mode === 'dark' ? 'dark' : 'light';
  return {
    backgroundColor:
      accessibilityHoverBackgroundColor ||
      colors.accessibility.hover.radioBackgroundColor[mode],
    color:
      accessibilityHoverColor || colors.accessibility.hover.radioColor[mode],
  };
};

export interface ButtonAccessibilityHoverProps {
  accessibilityHoverDisabled?: boolean;
  accessibilityHoverBackgroundColor?: string;
  accessibilityHoverColor?: string;
  accessibilityHoverBorderColor?: string;
}

// Buttons only ever get this when an override prop is explicitly supplied -
// no default, so existing button hover never changes on its own.
export const getAccessibilityButtonHoverExtra = (
  accessibilityMode: boolean,
  props: ButtonAccessibilityHoverProps = {}
) => {
  const {
    accessibilityHoverDisabled = false,
    accessibilityHoverBackgroundColor,
    accessibilityHoverColor,
    accessibilityHoverBorderColor,
  } = props;

  const hasOverride =
    accessibilityHoverBackgroundColor ||
    accessibilityHoverColor ||
    accessibilityHoverBorderColor;

  if (!accessibilityMode || accessibilityHoverDisabled || !hasOverride)
    return {};

  return {
    ...(accessibilityHoverBackgroundColor && {
      backgroundColor: accessibilityHoverBackgroundColor,
    }),
    ...(accessibilityHoverColor && { color: accessibilityHoverColor }),
    ...(accessibilityHoverBorderColor && {
      borderColor: accessibilityHoverBorderColor,
      boxShadow: `0 0 0 0.4rem ${accessibilityHoverBorderColor}30`,
    }),
  };
};
